import CustomTooltip from '../../../shared/CustomTooltip';
import React, { useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    Stack,
    Typography,
    lighten,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AddCircle,
    DragIndicator,
    KeyboardArrowDown,
    KeyboardArrowUp,
} from '@mui/icons-material';
import {
    UseFieldArrayRemove,
    UseFieldArraySwap,
    useFieldArray,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { PricingTemplate } from '../../../types/contractor';
import FormTextField from '../../../shared/FormTextField';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { makeStyles } from '@material-ui/core';
import CutoffInput from './CutoffInput';

const Item = ({
    index,
    remove,
    swap,
}: {
    index: number;
    remove: UseFieldArrayRemove;
    swap: UseFieldArraySwap;
}) => {
    const { control } = useFormContext<PricingTemplate>();

    const values = useWatch({ control });

    const ref = useRef<HTMLDivElement | null>(null);
    const theme = useTheme();

    useEffect(() => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    return (
        <Card
            variant='outlined'
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider',
            }}
            ref={ref}
        >
            <SortableKnob>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1.5,
                        bgcolor: lighten(theme.palette.primary.main, 0.9),
                        cursor: 'grab',
                    }}
                >
                    <DragIndicator color='primary' />
                </Box>
            </SortableKnob>
            <CardContent sx={{ width: '100%' }}>
                <Stack spacing={1.5}>
                    <Box>
                        <InputLabel
                            required
                            htmlFor={`items-${index}-name__input`}
                        >
                            Name
                        </InputLabel>
                        <FormTextField
                            id={`items-${index}-name__input`}
                            name={`items[${index}].name`}
                            control={control}
                            placeholder='Regular service'
                        />
                        <FormHelperText>
                            Name of the service you offer
                        </FormHelperText>
                    </Box>
                    <Box>
                        <InputLabel
                            htmlFor={`items-${index}-description__input`}
                        >
                            Description
                        </InputLabel>
                        <FormTextField
                            id={`items-${index}-description__input`}
                            name={`items[${index}].description`}
                            control={control}
                            placeholder='Basic service price per hour'
                            multiline
                            rows={2}
                        />
                        <FormHelperText>
                            What does this service include?
                        </FormHelperText>
                    </Box>
                    <Box>
                        <InputLabel
                            required
                            htmlFor={`items-${index}-cost__input`}
                        >
                            Cost (ex. GST)
                        </InputLabel>
                        <FormTextField
                            id={`items-${index}-cost__input`}
                            name={`items[${index}].cost`}
                            type='number'
                            placeholder='100.00'
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Typography>$</Typography>
                                    </InputAdornment>
                                ),
                            }}
                            control={control}
                        />
                    </Box>
                    <Box>
                        <InputLabel htmlFor={`items-${index}-cutoff__input`}>
                            Job request cut-off
                        </InputLabel>
                        <CutoffInput
                            id={`items-${index}-cutoff__input`}
                            name={`items[${index}].cutoff`}
                            placeholder='Same as template'
                            options={[
                                { value: 0, label: 'Same day' },
                                {
                                    value: 1,
                                    label: '1 business day',
                                },
                                {
                                    value: 2,
                                    label: '2 business days',
                                },
                                {
                                    value: 3,
                                    label: '3 business days',
                                },
                                {
                                    value: 4,
                                    label: '4 business days',
                                },
                                {
                                    value: 5,
                                    label: '5 business days',
                                },
                                {
                                    value: 6,
                                    label: '6 business days',
                                },
                                {
                                    value: 7,
                                    label: '7 business days',
                                },
                            ]}
                        />
                        <FormHelperText>
                            Override the template or organisation cut-off for
                            this service, counted in business days.
                        </FormHelperText>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                        }}
                    >
                        {values?.items && values.items.length > 1 && (
                            <>
                                <CustomTooltip title='Move item up'>
                                    <IconButton
                                        color='default'
                                        disabled={index === 0}
                                        onClick={() => swap(index, index - 1)}
                                    >
                                        <KeyboardArrowUp />
                                    </IconButton>
                                </CustomTooltip>
                                <CustomTooltip title='Move item down'>
                                    <IconButton
                                        disabled={
                                            index === values.items.length - 1
                                        }
                                        onClick={() => swap(index, index + 1)}
                                    >
                                        <KeyboardArrowDown />
                                    </IconButton>
                                </CustomTooltip>
                            </>
                        )}
                        <Button
                            color='error'
                            onClick={() => remove(index)}
                            variant='outlined'
                        >
                            Delete
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        gap: 8,
    },
    item: {
        userSelect: 'none',
    },
    dragged: {
        zIndex: 1400,
    },
});

const PricingTemplateItems = () => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const {
        control,
        formState: { errors },
    } = useFormContext<PricingTemplate>();

    const { fields, append, remove, move, swap } = useFieldArray({
        name: 'items',
        control,
    });

    const onSortEnd = (a: number, b: number) => {
        move(a, b);
    };

    return (
        <>
            <Stack spacing={1.5}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    {isMobile || isIpad ? (
                        <IconButton
                            color='primary'
                            size='small'
                            onClick={() =>
                                append({
                                    name: '',
                                    description: '',
                                    cost: 0,
                                    cutoff: null,
                                })
                            }
                        >
                            <AddCircle fontSize='small' />
                        </IconButton>
                    ) : (
                        <CustomButton
                            variant='contained'
                            color='primary'
                            onClick={() =>
                                append({
                                    name: '',
                                    description: '',
                                    cost: 0,
                                    cutoff: null,
                                })
                            }
                        >
                            Add Service
                        </CustomButton>
                    )}
                </Box>
                <SortableList
                    onSortEnd={onSortEnd}
                    draggedItemClassName={classes.dragged}
                    className={classes.list}
                >
                    {fields.map((field, index) => (
                        <SortableItem key={field.id}>
                            <div className={classes.item}>
                                <Item
                                    index={index}
                                    remove={remove}
                                    swap={swap}
                                />
                            </div>
                        </SortableItem>
                    ))}
                </SortableList>
            </Stack>
            {errors.items && (
                <FormHelperText error>{errors.items.message}</FormHelperText>
            )}
        </>
    );
};

export default PricingTemplateItems;
